import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  FormHelperText,
  Typography,
  CircularProgress,
} from "@mui/material";
import { Formik, Form } from "formik";
import * as yup from "yup";
import Swal from "sweetalert2";
import axios from "axios";
import { AccountsandFinanceurl } from "../../../../config";

const base_url = AccountsandFinanceurl.uri;

const SupplierForm = ({ isEditing, data, onClose, onAction }) => {
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const userId = parseInt(decodedToken.Id);
  const companyId = parseInt(decodedToken.CompanyId);

  const [statusOptions, setStatusOptions] = useState([]);
  const [loadingStatus, setLoadingStatus] = useState(true);

  // ✅ Validation Schema
  const validationSchema = yup.object().shape({
    supplierName: yup
      .string()
      .required("Supplier name is required"),

    contactNumber: yup
      .string()
      .matches(
        /^(07\d{8}|\+2547\d{8})$/,
        "Use format 0712341234 or +254712341234"
      )
      .required("Contact number is required"),

    email: yup
      .string()
      .email("Enter a valid email address")
      .required("Email is required"),

    registrationNumber: yup
      .string()
     
      .required("National ID / Registration number is required"),

    taxIdentificationNumber: yup
      .string()
      .matches(
        /^[A-Z]\d{10}[A-Z]$/,
        "Enter a Valid TIN number"
      )
      .required("Tax Identification Number is required"),

    contactPersonName: yup
      .string()
      .nullable(),

    contactPersonEmail: yup
      .string()
      .email("Enter a valid email")
      .nullable(),

    contactPersonPhone: yup
      .string()
      .matches(
        /^(07\d{8}|\+2547\d{8})$/,
        "Enter a Valid Contact Person Phone number"
      )
      .required("Contact person phone is required"),

    industrySector: yup
      .string()
      .nullable(),

    notes: yup
      .string()
      .nullable(),

    statusFlag: yup
      .number()
      .required("Status is required"),

    idDocumentFile: yup.mixed().nullable(),
  });


  // ✅ Fetch live Status data
  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        setLoadingStatus(true);
        const token = localStorage.getItem("token")?.replace(/^"|"$/g, "");
        const query = `
          {
            allStatusFlags {
              id
              name
              value
            }
          }
        `;

        const { data: response } = await axios.post(
          base_url,
          { query },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const statuses = response?.data?.allStatusFlags || [];

        if (statuses.length > 0) {
          setStatusOptions(
            statuses.map((status) => ({
              label: status.name,
              value: status.value,
            }))
          );
        } else {
          // fallback if no data
          setStatusOptions([
            { label: "Active", value: 1 },
            { label: "Inactive", value: 0 },
          ]);
        }
      } catch (error) {
        console.error("Error fetching status options:", error);
        setStatusOptions([
          { label: "Active", value: 1 },
          { label: "Inactive", value: 0 },
        ]);
      } finally {
        setLoadingStatus(false);
      }
    };

    fetchStatuses();
  }, []);

  // ✅ Initial Values
  const initialValues = data
    ? {
        supplierName: data.supplierName || "",
        contactNumber: data.contactNumber || "",
        email: data.email || "",
        address: data.address || "",
        registrationNumber: data.registrationNumber || "",
        taxIdentificationNumber: data.taxIdentificationNumber || "",
        contactPersonName: data.contactPersonName || "",
        contactPersonEmail: data.contactPersonEmail || "",
        contactPersonPhone: data.contactPersonPhone || "",
        industrySector: data.industrySector || "",
        notes: data.notes || "",
        statusFlag: data.statusFlag || "",
       // idDocumentFile: data.idDocumentFile || null,
      }
    : {
        supplierName: "",
        contactNumber: "",
        email: "",
        address: "",
        registrationNumber: "",
        taxIdentificationNumber: "",
        contactPersonName: "",
        contactPersonEmail: "",
        contactPersonPhone: "",
        industrySector: "",
        notes: "",
        statusFlag: "",
      //  idDocumentFile: null,
      };

  // ✅ Submit Handler
  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const inputObject = {
        ...values,
        statusFlag: Number(values.statusFlag) || 0,
        companyId,
      };

      // TODO: Handle file upload separately (e.g., via multipart form or presigned URL)
      if (values.idDocumentFile) {
        console.log("ID Document File selected for upload:", values.idDocumentFile.name);
        // Example: Implement file upload logic here and add file reference to inputObject if needed
      }

      const commonFields = `
        id
        supplierName
        contactNumber
        email
        address
        registrationNumber
        taxIdentificationNumber
        contactPersonName
        contactPersonEmail
        contactPersonPhone
        industrySector
        dateAdded
        notes
        isActive
        statusFlag
        createdBy
        createdDate
        updatedBy
        updatedDate
        companyId
        company
      `;

      const mutationString = isEditing
        ? `
          mutation UpdateSuppliers($updatedVendors: SuppliersInput!) {
            updateSuppliers(updatedVendors: $updatedVendors) {
              ${commonFields}
            }
          }
        `
        : `
          mutation CreateSuppliers($newVendors: SuppliersInput!) {
            createSuppliers(newVendors: $newVendors) {
              ${commonFields}
            }
          }
        `;

      const variables = isEditing
        ? { updatedVendors: { ...inputObject, id: data.id } }
        : { newVendors: inputObject };

      const responseField = isEditing ? "updateSuppliers" : "createSuppliers";
      const token = localStorage.getItem("token")?.replace(/^"|"$/g, "");

      const { data: response } = await axios.post(
        base_url,
        { query: mutationString, variables },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const result = response?.data?.[responseField];
      if (result) {
        onClose();
        onAction();
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: `Supplier ${isEditing ? "updated" : "created"} successfully.`,
        });
      }
    } catch (error) {
      console.error("Mutation error:", error);
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while processing your request. Please try again later.",
      });
    }
    setSubmitting(false);
  };

  // ✅ Form Fields (dynamic status)
  const fields = [
    { name: "supplierName", label: "Supplier Name", type: "text" },
    { name: "contactNumber", label: "Contact Number", type: "text" },
    { name: "email", label: "Email", type: "email" },
    { name: "address", label: "Address", type: "text" },
    { name: "registrationNumber", label: "National ID / Registration No.", type: "text" },
    { name: "taxIdentificationNumber", label: "Tax Identification Number", type: "text" },
    { name: "contactPersonName", label: "Contact Person Name", type: "text" },
    { name: "contactPersonEmail", label: "Contact Person Email", type: "email" },
    { name: "contactPersonPhone", label: "Contact Person Phone", type: "text" },
    { name: "industrySector", label: "Industry Sector", type: "text" },
    { name: "notes", label: "Notes", type: "text", multiline: true, rows: 5 },
    { name: "statusFlag", label: "Status", type: "select", options: statusOptions },
  ];

  return (
    <Box m="20px">
      <Typography variant="h4" fontWeight="bold" mb={1}>
        {isEditing ? "Edit Supplier" : "Create New Supplier"}
      </Typography>
      <Typography variant="subtitle1" mb={3} color="text.secondary">
        {isEditing ? "Edit an existing supplier" : "Create a new supplier record"}
      </Typography>

      {loadingStatus ? (
        <Box display="flex" justifyContent="center" mt={5}>
          <CircularProgress />
        </Box>
      ) : (
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            handleSubmit,
            isSubmitting,
            setFieldValue,
          }) => {
            const renderField = (field) => (
              <Box key={field.name}>
                {field.type === "select" ? (
                  <FormControl
                    fullWidth
                    error={Boolean(touched[field.name] && errors[field.name])}
                  >
                    <InputLabel>{field.label}</InputLabel>
                    <Select
                      name={field.name}
                      value={values[field.name]}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      label={field.label}
                    >
                      <MenuItem value="" disabled>
                        Select {field.label}
                      </MenuItem>
                      {field.options.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {touched[field.name] && errors[field.name]
                        ? errors[field.name]
                        : ""}
                    </FormHelperText>
                  </FormControl>
                ) : (
                  <TextField
                    fullWidth
                    type={field.type || "text"}
                    name={field.name}
                    label={field.label}
                    value={values[field.name]}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    multiline={field.multiline || false}
                    rows={field.rows || 1}
                    error={Boolean(touched[field.name] && errors[field.name])}
                    helperText={
                      touched[field.name] && errors[field.name]
                        ? errors[field.name]
                        : ""
                    }
                  />
                )}
              </Box>
            );

            const showUpload = values.registrationNumber.trim().length > 0;
            const nextFieldIndex = showUpload ? 5 : 6;

            return (
              <Form onSubmit={handleSubmit}>
                <Box display="grid" gap="20px" gridTemplateColumns="repeat(2, 1fr)">
                  {/* First 4 fields: supplierName to address */}
                  {fields.slice(0, 4).map(renderField)}

                  {/* Registration Number */}
                  {renderField(fields[4])}

                  {/* Conditional: Upload or Tax Identification Number */}
                  {showUpload ? (
                    <Box>
                      <Typography variant="subtitle2" mb={1} color="text.secondary">
                        Upload your ID/ Document (Optional)
                      </Typography>
                      <input
                        type="file"
                        accept=".jpg,.jpeg,.png,.pdf"
                        onChange={(event) => setFieldValue("idDocumentFile", event.target.files[0])}
                        style={{
                          width: "100%",
                          padding: "12px",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                          backgroundColor: "white",
                        }}
                      />
                      {values.idDocumentFile && (
                        <Typography variant="body2" color="text.secondary" mt={1}>
                          Uploaded: {values.idDocumentFile.name}
                        </Typography>
                      )}
                      {touched.idDocumentFile && errors.idDocumentFile && (
                        <FormHelperText error>{errors.idDocumentFile}</FormHelperText>
                      )}
                    </Box>
                  ) : (
                    renderField(fields[5]) // taxIdentificationNumber
                  )}

                  {/* Remaining fields starting from next index */}
                  {fields.slice(nextFieldIndex).map(renderField)}
                </Box>

                <Box display="flex" justifyContent="flex-end" mt={4}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting}
                    startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
                  >
                    {isSubmitting
                      ? isEditing
                        ? "Updating..."
                        : "Saving..."
                      : isEditing
                      ? "Update Supplier"
                      : "Save Supplier"}
                  </Button>
                </Box>
              </Form>
            );
          }}
        </Formik>
      )}
    </Box>
  );
};

export default SupplierForm;